import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { finalize } from 'rxjs';
import { LoadingService } from '@ui-kit/framework/util/service/loading.service';
import {
    AtokuEmailTemplateDocument,
    AtokuEmailTemplateKey,
    AtokuEmailTemplateUpdateRequest,
    AtokuService,
    AtokuTermsAndConditionsUpdateRequest
} from '../services/atoku.service';
import {
    TermsAndConditionsDialogComponent,
    TermsAndConditionsDialogData
} from './dialogs/terms-and-conditions-dialog/terms-and-conditions-dialog.component';

function maxWordsValidator(maxWords: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = String(control.value ?? '').trim();
        if (!value) {
            return null;
        }

        const wordCount = value.split(/\s+/).filter(Boolean).length;
        return wordCount <= maxWords ? null : { maxWords: { maxWords, actual: wordCount } };
    };
}

function cellphoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = String(control.value ?? '').trim();
        if (!value) {
            return null;
        }

        return /^[+\d][\d\s().-]{6,24}$/.test(value)
            ? null
            : { cellphoneNumber: true };
    };
}

@Component({
    selector: 'app-atoku-booking-config',
    templateUrl: './atoku-booking-config.component.html',
    styleUrls: ['./atoku-booking-config.component.scss']
})
export class AtokuBookingConfigComponent {
    readonly form: FormGroup;
    readonly emailTemplateForm: FormGroup;
    termsLoading = false;
    termsSaving = false;
    emailTemplateLoading: AtokuEmailTemplateKey | null = null;
    emailTemplateSaving: AtokuEmailTemplateKey | null = null;
    activeEmailTemplateKey: AtokuEmailTemplateKey | null = null;
    activeEmailTemplateTitle = '';
    activeEmailTemplateDocument: AtokuEmailTemplateDocument | null = null;

    constructor(
        private fb: FormBuilder,
        private atokuService: AtokuService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
        private sanitizer: DomSanitizer,
        public loader: LoadingService
    ) {
        this.form = this.fb.group({
            bufferTimeHours: [1, [Validators.required, Validators.min(1), Validators.max(8)]],
            maximumLeadTimeHours: [24, [Validators.required, Validators.min(1)]],
            allowedTeamMembers: [0, [Validators.required, Validators.min(0)]],
            refundMinimumNoticeHours: [24, [Validators.required, Validators.min(0)]],
            lateCancellationFeeAmountType: ['fixed', [Validators.required]],
            lateCancellationFeeAmount: [0, [Validators.required, Validators.min(0)]],
            requiredDepositAmountType: ['fixed', [Validators.required]],
            requiredDepositAmount: [0, [Validators.required, Validators.min(0)]],
            allowAdditionalMembersToRequestAccess: [false],
            allowAdditionalMembersToShareWithTeam: [false],
            companyId: [{ value: '', disabled: true }],
            companyName: [{ value: '', disabled: true }],
            offerTextAccessPass: [
                '',
                [Validators.required, maxWordsValidator(10)]
            ],
            signupRouteAccessPass: [
                '',
                [Validators.required]
            ],
            smsNotificationsEnabled: [false],
            opsTeamCellphoneNumbers: this.fb.array([]),
            allowedCardTypes: [{ value: '', disabled: true }],
            clientReferenceFormat: [{ value: '', disabled: true }]
        });

        this.form.get('requiredDepositAmountType')?.valueChanges.subscribe(() => {
            this.updateRequiredDepositValidators();
        });
        this.form.get('lateCancellationFeeAmountType')?.valueChanges.subscribe(() => {
            this.updateLateCancellationFeeValidators();
        });
        this.updateRequiredDepositValidators();
        this.updateLateCancellationFeeValidators();

        this.emailTemplateForm = this.fb.group({
            name: [''],
            subject: [''],
            previewText: [''],
            html: ['']
        });

        this.loadConfig();
    }

    get offerWordCount(): number {
        const value = String(this.form.get('offerTextAccessPass')?.value ?? '').trim();
        return value ? value.split(/\s+/).filter(Boolean).length : 0;
    }

    get opsTeamCellphoneNumbers(): FormArray {
        return this.form.get('opsTeamCellphoneNumbers') as FormArray;
    }

    get termsButtonText(): string {
        return this.termsSaving ? 'Saving' : "T's & C's";
    }

    get approvalEmailButtonText(): string {
        return this.emailTemplateSaving === 'approvalEmail' ? 'Saving' : 'Approval Email';
    }

    get confirmationEmailButtonText(): string {
        return this.emailTemplateSaving === 'bookingConfirmationEmail' ? 'Saving' : 'Booking Confirmation Email';
    }

    get activeEmailTemplateStatus(): string {
        if (!this.activeEmailTemplateDocument?.exists) {
            return 'New template';
        }

        return `Version ${this.activeEmailTemplateDocument.version || 1}`;
    }

    get compiledEmailPreviewHtml(): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(
            String(this.emailTemplateForm.get('html')?.value ?? '')
        );
    }

    save(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const values = this.form.getRawValue();
        const opsTeamCellphoneNumbers = this.getCleanOpsTeamCellphoneNumbers();
        if (values.smsNotificationsEnabled && opsTeamCellphoneNumbers.length === 0) {
            this.snackBar.open('Add at least one Ops team cellphone number before enabling SMS notifications.', 'Dismiss', { duration: 4000 });
            return;
        }

        this.atokuService.updateBookingConfig({
            bufferTimeHours: Number(values.bufferTimeHours),
            maximumLeadTimeHours: Number(values.maximumLeadTimeHours),
            smsNotificationsEnabled: Boolean(values.smsNotificationsEnabled),
            opsTeamCellphoneNumbers,
            allowedTeamMembers: Number(values.allowedTeamMembers),
            refundMinimumNoticeHours: Number(values.refundMinimumNoticeHours),
            lateCancellationFeeAmount: Number(values.lateCancellationFeeAmount),
            lateCancellationFeeAmountType: this.normalizeAmountType(values.lateCancellationFeeAmountType),
            requiredDepositAmount: Number(values.requiredDepositAmount),
            requiredDepositAmountType: this.normalizeAmountType(values.requiredDepositAmountType),
            allowAdditionalMembersToRequestAccess: Boolean(values.allowAdditionalMembersToRequestAccess),
            allowAdditionalMembersToShareWithTeam: Boolean(values.allowAdditionalMembersToShareWithTeam),
            offerTextAccessPass: String(values.offerTextAccessPass),
            signupRouteAccessPass: String(values.signupRouteAccessPass)
        }).subscribe(() => this.loadConfig());
    }

    addOpsTeamCellphoneNumber(value = ''): void {
        this.opsTeamCellphoneNumbers.push(this.fb.control(value, [cellphoneNumberValidator()]));
    }

    removeOpsTeamCellphoneNumber(index: number): void {
        this.opsTeamCellphoneNumbers.removeAt(index);
    }

    trackByIndex(index: number): number {
        return index;
    }

    openTermsAndConditions(): void {
        if (this.termsLoading || this.termsSaving) {
            return;
        }

        this.termsLoading = true;
        this.atokuService.getTermsAndConditions()
            .pipe(finalize(() => this.termsLoading = false))
            .subscribe({
                next: document => {
                    const dialogRef = this.dialog.open<
                        TermsAndConditionsDialogComponent,
                        TermsAndConditionsDialogData,
                        AtokuTermsAndConditionsUpdateRequest
                    >(TermsAndConditionsDialogComponent, {
                        width: '920px',
                        maxWidth: '96vw',
                        data: { document }
                    });

                    dialogRef.afterClosed().subscribe(result => {
                        if (!result) {
                            return;
                        }

                        this.saveTermsAndConditions(result);
                    });
                },
                error: () => this.snackBar.open("Could not load T's & C's.", 'Dismiss', { duration: 4000 })
            });
    }

    openEmailTemplate(templateKey: AtokuEmailTemplateKey, title: string): void {
        if (this.emailTemplateLoading || this.emailTemplateSaving) {
            return;
        }

        this.emailTemplateLoading = templateKey;
        this.atokuService.getEmailTemplate(templateKey)
            .pipe(finalize(() => this.emailTemplateLoading = null))
            .subscribe({
                next: document => {
                    this.activeEmailTemplateKey = templateKey;
                    this.activeEmailTemplateTitle = title;
                    this.activeEmailTemplateDocument = document;
                    this.emailTemplateForm.reset({
                        name: document.name || title,
                        subject: document.subject || title,
                        previewText: document.previewText || '',
                        html: document.html || ''
                    });
                },
                error: () => this.snackBar.open(`Could not load ${title}.`, 'Dismiss', { duration: 4000 })
            });
    }

    closeEmailTemplateEditor(): void {
        if (this.emailTemplateSaving) {
            return;
        }

        this.activeEmailTemplateKey = null;
        this.activeEmailTemplateTitle = '';
        this.activeEmailTemplateDocument = null;
        this.emailTemplateForm.reset({
            name: '',
            subject: '',
            previewText: '',
            html: ''
        });
    }

    saveActiveEmailTemplate(): void {
        if (!this.activeEmailTemplateDocument || !this.activeEmailTemplateKey || this.emailTemplateSaving) {
            return;
        }

        this.saveEmailTemplate(this.activeEmailTemplateKey, {
            name: this.getEmailTemplateFormString('name') || this.activeEmailTemplateTitle,
            subject: this.getEmailTemplateFormString('subject') || this.activeEmailTemplateTitle,
            previewText: this.getEmailTemplateFormString('previewText'),
            html: String(this.emailTemplateForm.get('html')?.value ?? ''),
            // This screen now treats raw HTML as the editable source of truth. Persist a neutral
            // Unlayer design so a stale historical design can no longer masquerade as the current email.
            designJson: JSON.stringify(this.blankDesign())
        });
    }

    private loadConfig(): void {
        this.atokuService.getBookingConfig().subscribe(config => {
            this.form.patchValue({
                bufferTimeHours: config.bufferTimeHours,
                maximumLeadTimeHours: config.maximumLeadTimeHours,
                allowedTeamMembers: config.allowedTeamMembers ?? 0,
                refundMinimumNoticeHours: config.refundMinimumNoticeHours ?? 24,
                lateCancellationFeeAmountType: this.normalizeAmountType(config.lateCancellationFeeAmountType),
                lateCancellationFeeAmount: config.lateCancellationFeeAmount ?? 0,
                requiredDepositAmount: config.requiredDepositAmount ?? 0,
                requiredDepositAmountType: this.normalizeAmountType(config.requiredDepositAmountType),
                allowAdditionalMembersToRequestAccess: !!config.allowAdditionalMembersToRequestAccess,
                allowAdditionalMembersToShareWithTeam: !!config.allowAdditionalMembersToShareWithTeam,
                companyId: config.companyId,
                companyName: config.companyName,
                offerTextAccessPass: config.offerTextAccessPass,
                signupRouteAccessPass: config.signupRouteAccessPass,
                smsNotificationsEnabled: Boolean(config.smsNotificationsEnabled),
                allowedCardTypes: (config.allowedCardTypes ?? []).join(', '),
                clientReferenceFormat: config.clientReferenceFormat
            });
            this.updateRequiredDepositValidators();
            this.updateLateCancellationFeeValidators();
            this.setOpsTeamCellphoneNumbers(config.opsTeamCellphoneNumbers ?? []);
        });
    }

    private saveTermsAndConditions(result: AtokuTermsAndConditionsUpdateRequest): void {
        this.termsSaving = true;
        this.atokuService.updateTermsAndConditions(result)
            .pipe(finalize(() => this.termsSaving = false))
            .subscribe({
                next: document => this.snackBar.open(
                    document.updateMessage || "T's & C's saved.",
                    'Dismiss',
                    { duration: document.upcomingLeasesUpdated ? 6000 : 3000 }
                ),
                error: () => this.snackBar.open("Could not save T's & C's.", 'Dismiss', { duration: 4000 })
            });
    }

    private saveEmailTemplate(templateKey: AtokuEmailTemplateKey, result: AtokuEmailTemplateUpdateRequest): void {
        this.emailTemplateSaving = templateKey;
        this.atokuService.updateEmailTemplate(templateKey, result)
            .pipe(finalize(() => this.emailTemplateSaving = null))
            .subscribe({
                next: document => {
                    this.activeEmailTemplateDocument = document;
                    this.emailTemplateForm.patchValue({
                        name: document.name || this.activeEmailTemplateTitle,
                        subject: document.subject || this.activeEmailTemplateTitle,
                        previewText: document.previewText || '',
                        html: document.html || ''
                    });
                    this.snackBar.open('Email template saved.', 'Dismiss', { duration: 3000 });
                },
                error: () => this.snackBar.open('Could not save email template.', 'Dismiss', { duration: 4000 })
            });
    }

    private getEmailTemplateFormString(controlName: string): string {
        return String(this.emailTemplateForm.get(controlName)?.value ?? '').trim();
    }

    private blankDesign(): unknown {
        return {
            counters: {
                u_row: 1,
                u_column: 1
            },
            body: {
                rows: [],
                values: {
                    backgroundColor: '#ffffff',
                    contentWidth: '600px',
                    fontFamily: {
                        label: 'Arial',
                        value: 'arial,helvetica,sans-serif'
                    }
                }
            }
        };
    }

    private setOpsTeamCellphoneNumbers(numbers: string[]): void {
        this.opsTeamCellphoneNumbers.clear();
        numbers
            .map(number => String(number ?? '').trim())
            .filter(Boolean)
            .forEach(number => this.addOpsTeamCellphoneNumber(number));
    }

    private getCleanOpsTeamCellphoneNumbers(): string[] {
        return this.opsTeamCellphoneNumbers.controls
            .map(control => String(control.value ?? '').trim())
            .filter(Boolean)
            .filter((number, index, list) => list.findIndex(item => item.toLowerCase() === number.toLowerCase()) === index);
    }

    private updateRequiredDepositValidators(): void {
        const amountControl = this.form.get('requiredDepositAmount');
        if (!amountControl) {
            return;
        }

        const validators = [Validators.required, Validators.min(0)];
        if (this.normalizeAmountType(this.form.get('requiredDepositAmountType')?.value) === 'percentage') {
            validators.push(Validators.max(100));
        }

        amountControl.setValidators(validators);
        amountControl.updateValueAndValidity({ emitEvent: false });
    }

    private updateLateCancellationFeeValidators(): void {
        const amountControl = this.form.get('lateCancellationFeeAmount');
        if (!amountControl) {
            return;
        }

        const validators = [Validators.required, Validators.min(0)];
        if (this.normalizeAmountType(this.form.get('lateCancellationFeeAmountType')?.value) === 'percentage') {
            validators.push(Validators.max(100));
        }

        amountControl.setValidators(validators);
        amountControl.updateValueAndValidity({ emitEvent: false });
    }

    private normalizeAmountType(value: unknown): 'fixed' | 'percentage' {
        return String(value ?? '').toLowerCase() === 'percentage' ? 'percentage' : 'fixed';
    }
}
